import { User, UcPackage, Order, SpinReward, SpinHistory, SpinResult } from '../types';
import api from './api-client'; 


// User endpoints
export const getUserByTelegramId = async (telegramId: number): Promise<User> => {
  const response = await api.get(`/users/telegram/${telegramId}`);
  return response.data;
};

// Spin endpoints
// Get paid roulette rewards
export const getSpinRewards = async (): Promise<SpinReward[]> => {
  const response = await api.get('/spin/rewards');
  return response.data;
};

// Get free roulette rewards
export const getFreeSpinRewards = async (): Promise<SpinReward[]> => {
  const response = await api.get('/spin/free-rewards');
  return response.data;
};

export const getSpinRewardsShort = async (): Promise<SpinReward[]> => {
  const response = await api.get('/spin/rewards/short');
  return response.data;
};

export const getRandomReward = async (): Promise<SpinReward> => {
  const response = await api.get(`/spin/rewards/random`);
  return response.data;
};

export const getRandomFreeReward = async (): Promise<SpinReward> => {
  const response = await api.get(`/spin/free-rewards/random`);
  return response.data;
};

export const spinWithReward = async (): Promise<{ spinHistory: SpinHistory, newBonusBalance: number }> => {
  try {
    const response = await api.post('/spin/spin-with-reward');
    return response.data;
  } catch (error) {
    // Re-throw the error to be handled by the caller
    throw error;
  }
};

export const freeSpinWithReward = async (): Promise<any> => {
  const response = await api.post('/spin/free-spin-with-reward');
  return response.data;
};

// Mock implementation for development
export const mockGetUserByTelegramId = (telegramId: number): User => {
  return {
    id: 'mock-user-id',
    telegramId,
    telegramUsername: 'test_user',
    bonusBalance: 1000,
    referralCode: 'TESTCODE',
    isBlocked: false,
    isAdmin: false
  };
};

export const createOrUpdateTelegramUser = async (userData: { telegramId: string, telegramUsername?: string, firstName?: string }): Promise<User> => {
  const response = await api.post('/users/telegram', userData);
  return response.data;
};

export const getUserSpinHistory = async (): Promise<SpinHistory[]> => {
  const response = await api.get(`/spin/history`);
  return response.data;
};

export const getUserLastFreeSpin = async (): Promise<{ lastFreeSpin: string | null, lastSpinResult?: any }> => {
  const response = await api.get(`/spin/free-history`);
  return {
    lastFreeSpin: response.data?.createdAt || null,
    lastSpinResult: response.data?.amount || null
  };
};

export const claimSpinReward = async (historyId: string, gameId?: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post('/spin/claim', { historyId, gameId });
    return response.data;
  } catch (error) {
    console.error('Error claiming spin reward:', error);
    throw error;
  }
};

export const mockGetSpinRewards = (): SpinReward[] => {
  // Free roulette rewards based on prize-lineup.txt
  return [
    {
      id: '1',
      rewardType: 'discount',
      rewardAmount: 8,
      probability: 7.0,
      minPurchase: 100,
      archived: false
    },
    {
      id: '2',
      rewardType: 'discount',
      rewardAmount: 10,
      probability: 10.0,
      minPurchase: 100,
      archived: false
    },
    {
      id: '3',
      rewardType: 'discount',
      rewardAmount: 12,
      probability: 15.0,
      minPurchase: 100,
      archived: false
    },
    {
      id: '4',
      rewardType: 'discount',
      rewardAmount: 15,
      probability: 25.0,
      minPurchase: 100,
      archived: false
    },
    {
      id: '5',
      rewardType: 'discount',
      rewardAmount: 18,
      probability: 15.0,
      minPurchase: 100,
      archived: false
    },
    {
      id: '6',
      rewardType: 'discount',
      rewardAmount: 24,
      probability: 10.0,
      minPurchase: 100,
      archived: false
    },
    {
      id: '7',
      rewardType: 'discount',
      rewardAmount: 28,
      probability: 8.0,
      minPurchase: 100,
      archived: false
    },
    {
      id: '8',
      rewardType: 'discount',
      rewardAmount: 35,
      probability: 6.0,
      minPurchase: 100,
      archived: false
    },
    {
      id: '9',
      rewardType: 'discount',
      rewardAmount: 46,
      probability: 3.0,
      minPurchase: 100,
      archived: false
    },
    {
      id: '10',
      rewardType: 'discount',
      rewardAmount: 100,
      probability: 1.0,
      minPurchase: 100,
      archived: false
    }
  ];
};

export const mockGetPaidRouletteRewards = (): SpinReward[] => {
  // Paid roulette rewards based on prize-lineup.txt
  return [
    {
      id: '1',
      rewardType: 'uc',
      rewardAmount: 60,
      probability: 44.0,
      minPurchase: 0,
      archived: false
    },
    {
      id: '2',
      rewardType: 'uc',
      rewardAmount: 985,
      probability: 5.5,
      minPurchase: 0,
      archived: false
    },
    {
      id: '3',
      rewardType: 'uc',
      rewardAmount: 8100,
      probability: 0.5,
      minPurchase: 0,
      archived: false
    },
    {
      id: '4',
      rewardType: 'discount',
      rewardAmount: 100,
      probability: 15.0,
      minPurchase: 100,
      archived: false
    },
    {
      id: '5',
      rewardType: 'discount',
      rewardAmount: 150,
      probability: 10.0,
      minPurchase: 100,
      archived: false
    },
    {
      id: '6',
      rewardType: 'discount',
      rewardAmount: 250,
      probability: 25.0,
      minPurchase: 100,
      archived: false
    }
  ];
};

export const mockSpinWithReward = (telegramId: number, rewardId: string): { spinHistory: SpinHistory, newBonusBalance: number } => {
  const user = mockGetUserByTelegramId(telegramId);
  const rewards = [...mockGetSpinRewards(), ...mockGetPaidRouletteRewards()];
  const reward = rewards.find(r => r.id === rewardId) || rewards[0];
  
  const spinHistory: SpinHistory = {
    id: 'mock-spin-history-id',
    telegramId,
    rewardId,
    claimed: false,
    bonusCost: 100,
    createdAt: new Date().toISOString(),
    amount: reward.rewardAmount,
    rewardType: reward.rewardType,
    
  };
  
  return {
    spinHistory,
    newBonusBalance: user.bonusBalance - 100
  };
};
